import clsx from "clsx";
import { useMemo } from "react";
import { useFittedChips } from "./hooks/useFittedChips";
import * as labeledStyles from "./LabeledTrigger.css";
import { Option, SelectSize } from "./SelectBase";

export interface LabeledTriggerValueProps {
  /**
   * Filter name shown as the eyebrow label above the value(s).
   */
  label: string;
  /**
   * Currently selected option values. Empty array renders the placeholder.
   */
  selectedValues: string[];
  /**
   * All options — used to look up labels for `selectedValues`.
   */
  options: Option[];
  /**
   * Text shown when no value is selected. Falls back to `label`.
   */
  placeholder?: string;
  /**
   * Trigger size — controls font sizes for label and value rows.
   */
  size?: SelectSize;
}

/**
 * Trigger content for the filter-bar variant of MultiSelect / SingleSelect:
 *
 * - Empty state: single-line placeholder (filter name), vertically centered.
 * - Filled state: two-row stack — eyebrow label on top, value row below.
 *
 * Both states render inside the same `labeledStack` (flex column, centered)
 * so they share the trigger's fixed height — the chip does not resize when
 * a value is picked.
 */
export function LabeledTriggerValue({
  label,
  selectedValues,
  options,
  placeholder,
  size = "sm"
}: LabeledTriggerValueProps): React.ReactElement {
  const labelByValue = useMemo(
    () => new Map(options.map((option) => [option.value, option.label])),
    [options]
  );
  const labels = selectedValues.map(
    (value) => labelByValue.get(value) ?? value
  );
  const { rowRef, visibleCount } = useFittedChips(labels);
  const hasValue = labels.length > 0;
  const overflowCount = labels.length - visibleCount;

  if (!hasValue) {
    return (
      <div className={labeledStyles.labeledStack}>
        <span
          className={clsx(
            labeledStyles.placeholderText,
            labeledStyles.valueRowSizes[size]
          )}
        >
          {placeholder ?? label}
        </span>
      </div>
    );
  }

  return (
    <div className={labeledStyles.labeledStack}>
      <span
        className={clsx(
          labeledStyles.eyebrowLabel,
          labeledStyles.eyebrowLabelSizes[size]
        )}
      >
        {label}
      </span>

      <div
        ref={rowRef}
        className={clsx(
          labeledStyles.valueRow,
          labeledStyles.valueRowSizes[size]
        )}
      >
        {labels.slice(0, visibleCount).map((optionLabel, index) => (
          <span
            key={selectedValues[index]}
            // A lone value still shrinks, so a long one ellipsizes.
            className={clsx(
              labeledStyles.valueText,
              visibleCount > 1 && labeledStyles.fittedValueText
            )}
          >
            {optionLabel}
          </span>
        ))}
        {overflowCount > 0 && (
          <span className={labeledStyles.overflowBadge}>+{overflowCount}</span>
        )}
      </div>
    </div>
  );
}
